import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

// Try these thumbnail qualities in order. YouTube returns a tiny 1097-byte
// placeholder JPEG with HTTP 404 when a video has no preview at that
// resolution — we treat all 404s as "missing" and fall back gracefully.
const THUMB_QUALITIES = ["maxresdefault", "hqdefault", "mqdefault", "sddefault"];

const ytThumb = (id, quality) =>
  `https://img.youtube.com/vi/${id}/${quality}.jpg`;

/**
 * Editorial Cinema card.
 *
 * Layout is a vertical block: crisp 16:9 cover on top, unfiltered so the
 * artwork reads clearly, and title + overline below on the card's own
 * surface. No dark washes, no red pulses, no Ken Burns — just the
 * thumbnail and the title. This is what the user asked for: "più pulito,
 * copertine e titoli si devono vedere bene".
 */
export default function VideoCard({ video, onOpen, index }) {
  const [tier, setTier] = useState(0);
  const [thumbMissing, setThumbMissing] = useState(false);

  const tryNext = () => {
    if (tier < THUMB_QUALITIES.length - 1) {
      setTier((t) => t + 1);
    } else {
      setThumbMissing(true);
    }
  };

  // YouTube returns its grey "video unavailable" placeholder (120x90) with
  // HTTP 404 + a tiny JPEG body. The browser still fires onLoad in that
  // case, so we have to inspect naturalWidth to detect it.
  const handleImgLoad = (e) => {
    const w = e.currentTarget.naturalWidth || 0;
    if (w > 0 && w <= 120) tryNext();
  };

  return (
    <motion.button
      type="button"
      onClick={() => onOpen(video)}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: 0.04 * index }}
      className="group text-left w-full flex flex-col"
      data-testid={`cinema-card-${video.id}`}
      style={{ background: "transparent" }}
    >
      {/* Cover — untouched, no dim gradient, no filter. Only a tasteful
          border keeps it grounded against the light background. */}
      <div
        className="relative overflow-hidden w-full"
        style={{
          aspectRatio: "16 / 9",
          background: "#F5F5F7",
          border: "1px solid rgba(0,0,0,0.06)",
          borderRadius: 14,
        }}
      >
        {thumbMissing ? (
          <div
            aria-hidden
            className="absolute inset-0 w-full h-full flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, #E5E5EA 0%, #F5F5F7 60%, #E9E9EE 100%)",
              color: "rgba(0,0,0,0.35)",
              fontSize: 11,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
            }}
          >
            Cinema
          </div>
        ) : (
          <img
            src={ytThumb(video.youtube_id, THUMB_QUALITIES[tier])}
            alt={video.title}
            loading="lazy"
            onError={tryNext}
            onLoad={handleImgLoad}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
          />
        )}

        {/* Discreet play medallion: only appears on hover (desktop) or is
            always visible in a subdued state on touch. Never occludes the
            centre of the artwork. */}
        <span
          aria-hidden
          className="absolute bottom-3 right-3 flex items-center justify-center transition-all duration-300 group-hover:scale-110"
          style={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.72)",
            color: "#FFFFFF",
            boxShadow: "0 8px 20px -6px rgba(0,0,0,0.45)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Play size={18} strokeWidth={1.6} fill="currentColor" />
        </span>
      </div>

      {/* Text block sits BELOW the cover, on the card surface — nothing
          hides the artwork. */}
      <div className="pt-4 sm:pt-5 px-1">
        <span
          className="text-[10px] uppercase block mb-2"
          style={{
            color: "#FF2800",
            letterSpacing: "0.28em",
            fontWeight: 500,
          }}
        >
          Cinema
          {video.vehicle_type ? (
            <span style={{ color: "#6E6E73", marginLeft: 8 }}>
              · {video.vehicle_type}
            </span>
          ) : null}
        </span>
        <h3
          className="text-lg sm:text-xl leading-snug"
          style={{
            color: "#1D1D1F",
            fontWeight: 400,
            letterSpacing: "-0.005em",
          }}
        >
          {video.title || "Garage Drops Cinema"}
        </h3>
        {video.description ? (
          <p
            className="text-sm mt-2 line-clamp-2"
            style={{ color: "#6E6E73", lineHeight: 1.55 }}
          >
            {video.description}
          </p>
        ) : null}
      </div>
    </motion.button>
  );
}
